import { useState } from 'react';

import Widget from '@/components/Widget/Widget';
import { useDashboardBreakpoint } from '@/features/dashboard/hooks/useDashboardBreakpoint';
import { cn } from '@/lib/cn';

import HslBusesDetailModal from './HslBusesDetailModal';
import { isReachable, minutesUntil } from './reachability';
import useHslBuses from './useHslBuses';
import useHslBusesConfig from './useHslBusesConfig';
import { useNow } from './useNow';

function fmtTime(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' });
}

export default function HslBusesWidget() {
  const [open, setOpen] = useState(false);
  const { stopId, walkMinutes, leaveBufferMinutes } = useHslBusesConfig();
  const { data, isLoading, isError, error, refetch, dataUpdatedAt } = useHslBuses(stopId);
  const now = useNow(15_000);
  const wide = useDashboardBreakpoint() === 'wide';
  const maxRows = wide ? 7 : 3;

  const status = isLoading ? 'loading' : isError ? 'error' : data && data.departures.length === 0 ? 'empty' : 'loaded';
  const allDepartures = data?.departures ?? [];

  const unreachableDeps = allDepartures.filter((d) => !isReachable(d, now, walkMinutes));
  const reachableDeps = allDepartures.filter((d) => isReachable(d, now, walkMinutes));
  const lastUnreachable = unreachableDeps.length > 0 ? [unreachableDeps[unreachableDeps.length - 1]] : [];
  // Wide has room for the closest can't-catch buses on top of a full list of catchable ones.
  const tileRows = wide
    ? [...unreachableDeps.slice(-3), ...reachableDeps.slice(0, maxRows)]
    : reachableDeps.length >= maxRows
      ? reachableDeps.slice(0, maxRows)
      : [...lastUnreachable, ...reachableDeps].slice(0, maxRows);

  return (
    <>
      <Widget
        title={data?.stopName ?? 'Next buses'}
        titleAccessory={data ? `walk ${walkMinutes} min` : undefined}
        status={status}
        errorMessage={error instanceof Error ? error.message : 'Failed to load departures'}
        emptyMessage="No departures in the next hour."
        loadingMessage="Loading departures…"
        onOpenDetail={() => setOpen(true)}
      >
        <ul className="flex h-full flex-col justify-evenly">
          {tileRows.map((d, i) => {
            const reachable = isReachable(d, now, walkMinutes);
            const mins = minutesUntil(d, now);
            const leaveIn = mins - walkMinutes - leaveBufferMinutes;
            const leaveNow = reachable && leaveIn <= 0;
            const caption = !reachable ? "can't catch" : leaveNow ? 'leave now' : `leave in ${leaveIn} min`;

            const ariaLabel = !reachable
              ? `Can't catch: ${d.routeShortName} to ${d.headsign}`
              : leaveNow
                ? `Leave now for ${d.routeShortName} to ${d.headsign}`
                : undefined;

            return (
              <li
                key={`${d.routeShortName}-${d.departureTimestamp}-${i}`}
                className={cn(
                  'tablet:grid-cols-[auto_1fr_auto_auto] grid grid-cols-[auto_1fr_auto] items-center gap-3 py-2',
                  !reachable && 'opacity-45',
                )}
                aria-label={ariaLabel}
              >
                <span className="font-display inline-flex min-w-14 items-center justify-center rounded-[10px] bg-(--accent-primary)/15 px-2 py-1.5 text-base font-bold text-(--accent-primary) no-underline">
                  {d.routeShortName}
                </span>
                <span className="truncate text-lg font-medium text-(--text-primary) no-underline">{d.headsign}</span>
                <span className="font-display tablet:inline hidden text-sm text-(--text-muted) tabular-nums no-underline">
                  {fmtTime(d.departureTimestamp)}
                </span>
                <span className="text-right no-underline">
                  <span className="font-display block text-xl leading-tight font-bold text-(--text-primary) tabular-nums">
                    {mins <= 0 ? 'now' : `${mins} min`}
                  </span>
                  <span
                    className={cn(
                      'block text-[11px] leading-tight',
                      leaveNow ? 'font-semibold tracking-wide text-(--accent-primary) uppercase' : 'text-(--text-muted)',
                    )}
                  >
                    {caption}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      </Widget>

      <HslBusesDetailModal
        open={open}
        onClose={() => setOpen(false)}
        stopName={data?.stopName}
        departures={data?.departures ?? []}
        lastUpdated={dataUpdatedAt ? new Date(dataUpdatedAt) : null}
        onRefresh={() => refetch()}
      />
    </>
  );
}
